import supabase from "./supabaseClient.js";

/**
 * ============================================================
 * CAREERS SERVICE (Pure Supabase Single Source of Truth)
 * ============================================================
 * Production service layer interfacing exclusively with:
 * - Supabase PostgreSQL database (`departments`, `jobs`, `job_skills`, `job_applications`, `job_application_status_history`)
 * - Supabase Storage (`career-resumes` private bucket)
 * - Atomic `submit_job_application` PostgreSQL RPC
 * - Controlled `update_job_application_review` PostgreSQL RPC
 *
 * NOTE: Zero mock data or localStorage fallbacks exist.
 * Errors are surfaced directly to the UI layer for appropriate handling.
 * ============================================================
 */

export const careersService = {
  // ==========================================================
  // 1. PUBLIC / FUTURE USE METHODS
  // ==========================================================

  /**
   * Fetch all publicly active and published jobs.
   * RLS automatically ensures only `status = 'Published' AND deleted_at IS NULL` are returned.
   */
  async getPublishedJobs() {
    const { data, error } = await supabase
      .from("jobs")
      .select(`
        id,
        title,
        slug,
        department_id,
        employment_type,
        workplace_type,
        location,
        experience_min,
        experience_max,
        salary_min,
        salary_max,
        salary_currency,
        salary_visible,
        short_description,
        full_description,
        responsibilities,
        requirements,
        preferred_qualifications,
        benefits,
        application_deadline,
        status,
        is_featured,
        display_order,
        meta_title,
        meta_description,
        departments (id, name, slug),
        job_skills (
          is_required,
          technologies (id, name, slug)
        )
      `)
      .eq("status", "Published")
      .is("deleted_at", null)
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[careersService] getPublishedJobs error:", error);
      throw error;
    }

    return data || [];
  },

  /**
   * Fetch a single published job by unique slug.
   */
  async getJobBySlug(slug) {
    if (!slug) return null;

    const { data, error } = await supabase
      .from("jobs")
      .select(`
        *,
        departments (id, name, slug),
        job_skills (
          is_required,
          technologies (id, name, slug)
        )
      `)
      .eq("slug", slug)
      .eq("status", "Published")
      .is("deleted_at", null)
      .maybeSingle();

    if (error) {
      console.error("[careersService] getJobBySlug error:", error);
      throw error;
    }

    return data;
  },

  /**
   * Fetch all corporate departments for filter chips and dropdowns.
   */
  async getDepartments() {
    const { data, error } = await supabase
      .from("departments")
      .select("id, name, slug, display_order")
      .order("display_order", { ascending: true });

    if (error) {
      console.error("[careersService] getDepartments error:", error);
      throw error;
    }

    return data || [];
  },

  /**
   * Fetch existing technology taxonomy from `public.technologies` for skills tagging.
   */
  async getTechnologies() {
    const { data, error } = await supabase
      .from("technologies")
      .select("id, name, slug")
      .order("name", { ascending: true });

    if (error) {
      console.error("[careersService] getTechnologies error:", error);
      throw error;
    }

    return data || [];
  },

  // ==========================================================
  // 2. ADMIN JOB MANAGEMENT METHODS
  // ==========================================================

  /**
   * Fetch all jobs for Admin Console (all statuses: Draft, Published, Closed, Archived).
   * Supports search, department, workplace, status filtering, and soft-delete toggle.
   */
  async getAllJobsAdmin({
    search = "",
    status = "all", // 'all' | 'Draft' | 'Published' | 'Closed' | 'Archived' | 'trash'
    department = "all",
    workplace = "all",
    includeDeleted = false,
  } = {}) {
    let query = supabase
      .from("jobs")
      .select(`
        *,
        departments (id, name, slug),
        job_skills (
          is_required,
          technologies (id, name, slug)
        ),
        job_applications (count)
      `);

    if (includeDeleted || status === "trash") {
      query = query.not("deleted_at", "is", null);
    } else {
      query = query.is("deleted_at", null);
      if (status !== "all") {
        query = query.eq("status", status);
      }
    }

    if (department !== "all") {
      query = query.eq("department_id", department);
    }

    if (workplace !== "all") {
      query = query.eq("workplace_type", workplace);
    }

    if (search.trim()) {
      query = query.or(`title.ilike.%${search.trim()}%,slug.ilike.%${search.trim()}%`);
    }

    query = query
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error("[careersService] getAllJobsAdmin error:", error);
      throw error;
    }

    return (data || []).map((job) => ({
      ...job,
      applications_count: job.job_applications?.[0]?.count || 0,
    }));
  },

  /**
   * Fetch a single job by ID with skills for the admin editor.
   */
  async getJobById(id) {
    if (!id) return null;

    const { data, error } = await supabase
      .from("jobs")
      .select(`
        *,
        departments (id, name, slug),
        job_skills (
          technology_id,
          is_required,
          technologies (id, name, slug)
        )
      `)
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("[careersService] getJobById error:", error);
      throw error;
    }

    return data;
  },

  /**
   * Create a new job position and associate skills.
   */
  async createJob(jobData, skillIds = []) {
    const slug =
      jobData.slug?.trim() ||
      jobData.title
        ?.toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

    const payload = {
      title: jobData.title.trim(),
      slug,
      department_id: jobData.department_id,
      employment_type: jobData.employment_type || "Full-time",
      workplace_type: jobData.workplace_type || "Remote",
      location: jobData.location?.trim() || "Remote",
      experience_min: Number(jobData.experience_min) || 0,
      experience_max: jobData.experience_max ? Number(jobData.experience_max) : null,
      salary_min: jobData.salary_min ? Number(jobData.salary_min) : null,
      salary_max: jobData.salary_max ? Number(jobData.salary_max) : null,
      salary_currency: jobData.salary_currency || "INR",
      salary_visible: Boolean(jobData.salary_visible),
      short_description: jobData.short_description?.trim() || "",
      full_description: jobData.full_description?.trim() || "",
      responsibilities: jobData.responsibilities || [],
      requirements: jobData.requirements || [],
      preferred_qualifications: jobData.preferred_qualifications || [],
      benefits: jobData.benefits || [],
      application_deadline: jobData.application_deadline || null,
      status: jobData.status || "Draft",
      is_featured: Boolean(jobData.is_featured),
      display_order: Number(jobData.display_order) || 0,
      meta_title: jobData.meta_title?.trim() || null,
      meta_description: jobData.meta_description?.trim() || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data: createdJob, error: createError } = await supabase
      .from("jobs")
      .insert(payload)
      .select()
      .single();

    if (createError) {
      console.error("[careersService] createJob error:", createError);
      throw createError;
    }

    // Associate skills if provided
    if (skillIds.length > 0) {
      await this.syncJobSkills(createdJob.id, skillIds);
    }

    return createdJob;
  },

  /**
   * Update an existing job position and synchronize skills.
   */
  async updateJob(id, jobData, skillIds = null) {
    if (!id) throw new Error("Job ID is required");

    const payload = {
      title: jobData.title.trim(),
      slug: jobData.slug?.trim(),
      department_id: jobData.department_id,
      employment_type: jobData.employment_type,
      workplace_type: jobData.workplace_type,
      location: jobData.location?.trim(),
      experience_min: Number(jobData.experience_min) || 0,
      experience_max: jobData.experience_max ? Number(jobData.experience_max) : null,
      salary_min: jobData.salary_min ? Number(jobData.salary_min) : null,
      salary_max: jobData.salary_max ? Number(jobData.salary_max) : null,
      salary_currency: jobData.salary_currency || "INR",
      salary_visible: Boolean(jobData.salary_visible),
      short_description: jobData.short_description?.trim(),
      full_description: jobData.full_description?.trim(),
      responsibilities: jobData.responsibilities || [],
      requirements: jobData.requirements || [],
      preferred_qualifications: jobData.preferred_qualifications || [],
      benefits: jobData.benefits || [],
      application_deadline: jobData.application_deadline || null,
      status: jobData.status,
      is_featured: Boolean(jobData.is_featured),
      display_order: Number(jobData.display_order) || 0,
      meta_title: jobData.meta_title?.trim() || null,
      meta_description: jobData.meta_description?.trim() || null,
      updated_at: new Date().toISOString(),
    };

    const { data: updatedJob, error: updateError } = await supabase
      .from("jobs")
      .update(payload)
      .eq("id", id)
      .select()
      .single();

    if (updateError) {
      console.error("[careersService] updateJob error:", updateError);
      throw updateError;
    }

    // Sync skills if array passed
    if (skillIds !== null) {
      await this.syncJobSkills(id, skillIds);
    }

    return updatedJob;
  },

  /**
   * Synchronize technology skills associated with a job.
   */
  async syncJobSkills(jobId, skillIds = []) {
    // Delete existing
    await supabase.from("job_skills").delete().eq("job_id", jobId);

    if (skillIds.length === 0) return [];

    const skillRecords = skillIds.map((techId) => ({
      job_id: jobId,
      technology_id: techId,
      is_required: true,
    }));

    const { data, error } = await supabase
      .from("job_skills")
      .insert(skillRecords)
      .select();

    if (error) {
      console.error("[careersService] syncJobSkills error:", error);
      throw error;
    }

    return data || [];
  },

  /**
   * Update status directly (Draft, Published, Closed, Archived).
   */
  async updateJobStatus(id, status) {
    if (!id) throw new Error("Job ID is required");
    const VALID_STATUSES = ["Draft", "Published", "Closed", "Archived"];
    if (!VALID_STATUSES.includes(status)) {
      throw new Error(`Invalid status: ${status}. Must be one of: ${VALID_STATUSES.join(", ")}`);
    }

    const { data, error } = await supabase
      .from("jobs")
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("[careersService] updateJobStatus error:", error);
      throw error;
    }

    return data;
  },

  /**
   * Toggle featured flag.
   */
  async toggleFeatured(id, isFeatured) {
    if (!id) throw new Error("Job ID is required");
    const { data, error } = await supabase
      .from("jobs")
      .update({ is_featured: Boolean(isFeatured), updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("[careersService] toggleFeatured error:", error);
      throw error;
    }

    return data;
  },

  /**
   * Sequentially reorder jobs by updating display_order.
   */
  async reorderJobs(items) {
    if (!Array.isArray(items)) return;

    const updates = items.map((item, index) =>
      supabase
        .from("jobs")
        .update({ display_order: index + 1, updated_at: new Date().toISOString() })
        .eq("id", item.id)
    );

    const results = await Promise.all(updates);
    const hasError = results.some((r) => r.error);
    if (hasError) {
      console.error("[careersService] reorderJobs encountered errors");
      throw new Error("Failed to reorder jobs completely.");
    }
    return true;
  },

  /**
   * Soft delete a job position.
   */
  async softDeleteJob(id) {
    if (!id) throw new Error("Job ID is required");
    const { error } = await supabase
      .from("jobs")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error("[careersService] softDeleteJob error:", error);
      throw error;
    }
    return { success: true, id };
  },

  /**
   * Restore a soft-deleted job position.
   */
  async restoreJob(id) {
    if (!id) throw new Error("Job ID is required");
    const { error } = await supabase
      .from("jobs")
      .update({ deleted_at: null, updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error("[careersService] restoreJob error:", error);
      throw error;
    }
    return { success: true, id };
  },

  // ==========================================================
  // 3. ADMIN APPLICATION MANAGEMENT METHODS
  // ==========================================================

  /**
   * Fetch applications for Admin Console.
   */
  async getApplications({ jobId = null, status = "all", search = "" } = {}) {
    let query = supabase
      .from("job_applications")
      .select(`
        *,
        jobs (id, title, slug, status, departments (name))
      `)
      .order("created_at", { ascending: false });

    if (jobId) {
      query = query.eq("job_id", jobId);
    }

    if (status !== "all") {
      query = query.eq("status", status);
    }

    if (search.trim()) {
      query = query.or(
        `first_name.ilike.%${search.trim()}%,last_name.ilike.%${search.trim()}%,email.ilike.%${search.trim()}%,job_title_snapshot.ilike.%${search.trim()}%`
      );
    }

    const { data, error } = await query;

    if (error) {
      console.error("[careersService] getApplications error:", error);
      throw error;
    }

    return data || [];
  },

  /**
   * Update application status and internal notes via controlled RPC.
   * Prevents modifying candidate submission fields.
   */
  async updateApplicationReview(applicationId, newStatus, internalNotes = "") {
    if (!applicationId) throw new Error("Application ID is required");

    const { data, error } = await supabase.rpc("update_job_application_review", {
      p_application_id: applicationId,
      p_new_status: newStatus,
      p_internal_notes: internalNotes || "",
    });

    if (error) {
      console.error("[careersService] updateApplicationReview error:", error.message);
      throw new Error(error.message);
    }

    return data;
  },

  /**
   * Fetch status history timeline for an application.
   */
  async getApplicationStatusHistory(applicationId) {
    if (!applicationId) return [];

    const { data, error } = await supabase
      .from("job_application_status_history")
      .select("*")
      .eq("application_id", applicationId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("[careersService] getApplicationStatusHistory error:", error);
      throw error;
    }

    return data || [];
  },

  /**
   * Generate short-lived signed URL for private resume download.
   */
  async createResumeSignedUrl(storagePath, expiresIn = 3600) {
    if (!storagePath) return null;

    const { data, error } = await supabase.storage
      .from("career-resumes")
      .createSignedUrl(storagePath, expiresIn);

    if (error) {
      console.error("[careersService] createResumeSignedUrl error:", error);
      throw error;
    }

    return data?.signedUrl || null;
  },

  /**
   * Generate short-lived signed URL for private cover letter download.
   */
  async createCoverLetterSignedUrl(storagePath, expiresIn = 3600) {
    return this.createResumeSignedUrl(storagePath, expiresIn);
  },

  // ==========================================================
  // 4. CANDIDATE APPLICATION SUBMISSION & ROLLBACK METHODS
  // ==========================================================

  /**
   * Upload resume to private `career-resumes` bucket with controlled UUID filename.
   */
  async uploadResumeFile(file, jobId = null) {
    if (!file) throw new Error("Resume file is required");

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error("Resume file size exceeds the 5MB limit");
    }

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      throw new Error("Only PDF, DOC, and DOCX files are allowed");
    }

    const uniqueId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    const targetFolder = jobId ? jobId : "general";
    const storagePath = `resumes/${targetFolder}/${uniqueId}.${ext}`;

    const { data, error } = await supabase.storage
      .from("career-resumes")
      .upload(storagePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      console.error("[careersService] uploadResumeFile error:", error);
      throw new Error(`Resume upload failed: ${error.message}`);
    }

    return {
      storagePath: data?.path || storagePath,
      originalFilename: file.name,
    };
  },

  /**
   * Alias for uploadResumeFile.
   */
  async uploadResume(file, jobId = null) {
    return this.uploadResumeFile(file, jobId);
  },

  /**
   * Upload optional cover letter to private `career-resumes` bucket with controlled UUID filename.
   */
  async uploadCoverLetter(file, jobId = null) {
    if (!file) return null;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error("Cover letter file size exceeds the 5MB limit");
    }

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      throw new Error("Only PDF, DOC, and DOCX files are allowed for cover letters");
    }

    const uniqueId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    const targetFolder = jobId ? jobId : "general";
    const storagePath = `resumes/${targetFolder}/cover-${uniqueId}.${ext}`;

    const { data, error } = await supabase.storage
      .from("career-resumes")
      .upload(storagePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      console.error("[careersService] uploadCoverLetter error:", error);
      throw new Error(`Cover letter upload failed: ${error.message}`);
    }

    return {
      storagePath: data?.path || storagePath,
      originalFilename: file.name,
    };
  },

  /**
   * Alias for uploadCoverLetter.
   */
  async uploadCoverLetterFile(file, jobId = null) {
    return this.uploadCoverLetter(file, jobId);
  },

  /**
   * Delete uploaded file from private storage (rollback upon DB RPC failure).
   * Uses atomic rollback RPC with fallback to direct storage removal.
   */
  async rollbackUploadedFile(storagePath) {
    if (!storagePath) return;
    try {
      // First attempt controlled RPC rollback
      const { data, error } = await supabase.rpc("rollback_career_file", {
        p_storage_path: storagePath,
      });

      if (error || !data?.success) {
        // Fallback to storage SDK remove
        await supabase.storage.from("career-resumes").remove([storagePath]);
      }
      console.info("[careersService] Successfully rolled back uploaded file:", storagePath);
    } catch (err) {
      console.warn("[careersService] Failed to rollback file:", storagePath, err);
      // Secondary attempt directly via storage SDK
      try {
        await supabase.storage.from("career-resumes").remove([storagePath]);
      } catch {
        // ignore secondary fallback error
      }
    }
  },

  /**
   * Submit complete application via atomic PostgreSQL RPC `submit_job_application`.
   */
  async submitApplication(applicationPayload) {
    const { data, error } = await supabase.rpc("submit_job_application", {
      p_job_id: applicationPayload.job_id || null,
      p_first_name: applicationPayload.first_name,
      p_last_name: applicationPayload.last_name,
      p_email: applicationPayload.email,
      p_phone: applicationPayload.phone,
      p_location: applicationPayload.location,
      p_experience_level: applicationPayload.experience_level,
      p_current_salary: applicationPayload.current_salary || null,
      p_expected_salary: applicationPayload.expected_salary,
      p_availability_date: applicationPayload.availability_date || null,
      p_notice_period: applicationPayload.notice_period,
      p_work_mode_preference: applicationPayload.work_mode_preference,
      p_skills: applicationPayload.skills,
      p_education: applicationPayload.education,
      p_certifications: applicationPayload.certifications || null,
      p_current_company: applicationPayload.current_company || null,
      p_motivation: applicationPayload.motivation,
      p_portfolio_url: applicationPayload.portfolio_url || null,
      p_linkedin_url: applicationPayload.linkedin_url || null,
      p_github_url: applicationPayload.github_url || null,
      p_resume_storage_path: applicationPayload.resume_storage_path,
      p_resume_filename: applicationPayload.resume_filename || "resume.pdf",
      p_cover_letter_storage_path: applicationPayload.cover_letter_storage_path || null,
      p_cover_letter_filename: applicationPayload.cover_letter_filename || null,
    });

    if (error) {
      console.error("[careersService] RPC submit_job_application error:", error.message);
      throw new Error(error.message);
    }

    return data;
  },
};

export default careersService;
